import type { JSX } from 'react';
import {
  type PrimeXProductType,
  ProductTypes,
} from '../../../../../../../../../../types';
import { RaterXAdditionalInsuredsModalConfig } from '../../modals/coverage_wrappers/RaterXAdditionalInsuredsModal';
import { RaterXAdditionalNamedInsuredModalConfig } from '../../modals/coverage_wrappers/RaterXAdditionalNamedInsuredModal';
import { RaterXAmendInsuringAgreementModalConfig } from '../../modals/coverage_wrappers/RaterXAmendInsuringAgreementModal';
import { RaterXSpecifiedEntityExclusionsModalConfig } from '../../modals/coverage_wrappers/RaterXSpecifiedEntityExclusionsModal';
import { RaterXSpecifiedEventExclusionModalConfig } from '../../modals/coverage_wrappers/RaterXSpecifiedEventExclusionModal';
import { RaterXWaiverOfSubrogationModalConfig } from '../../modals/coverage_wrappers/RaterXWaiverOfSubrogationModal';
import AdditionalInsureds from '../line-items/AdditionalInsureds';
import AdditionalNamedInsured from '../line-items/AdditionalNamedInsured';
import AmendInsuringAgreement from '../line-items/AmendInsuringAgreement';
import SpecifiedEntityExclusion from '../line-items/SpecifiedEntityExclusion';
import SpecifiedEventExclusion from '../line-items/SpecifiedEventExclusion';
import SpecifiedEventExclusionV2 from '../line-items/SpecifiedEventExclusionV2';
import { RaterXSpecifiedEventExclusionV2ModalConfig } from '../../modals/coverage_wrappers/RaterXSpecifiedEventExclusionV2Modal';
import WaiverOfSubrogation from '../line-items/WaiverOfSubrogation';
import type { AmendatoryLineItemProps } from '../AmendatoryCoverageLineItemFactory';
import type { Nullable } from '../../../../../../../../../../types/common/utility';
import DefenseCounsel from '../line-items/DefenseCounsel';
import { RaterXDefenseCounselModalModalConfig } from '../../modals/coverage_wrappers/RaterXDefenseCounselModal';
import SpecifiedCyberOrForensicProvider from '../line-items/SpecifiedCyberOrForensicProvider';
import { RaterXCyberForensicProviderModalConfig } from '../../modals/coverage_wrappers/RaterXCyberForensicProviderModal';
import AmendCooperation from '../line-items/AmendCooperation';
import AmendNoticeProvision from '../line-items/AmendNoticeProvision';
import SpecificVendorEndorsement from '../line-items/SpecificVendorEndorsement';
import { RaterXSpecificVendorEndorsementModalConfig } from '../../modals/coverage_wrappers/RaterXSpecificVendorEndorsementModal';

export enum AmendatoryCoverages {
  ADDITIONAL_INSURED = 'ADDITIONAL_INSURED',
  DEFENSE_COUNSEL = 'DEFENSE_COUNSEL',
  SPECIFIED_EVENT_EXCLUSION = 'SPECIFIED_EVENT_EXCLUSION',
  SPECIFIED_ENTITY_EXCLUSION = 'SPECIFIED_ENTITY_EXCLUSION',
  AMEND_INSURING_AGREEMENT = 'AMEND_INSURING_AGREEMENT',
  WAIVER_OF_SUBROGATION = 'WAIVER_OF_SUBROGATION',
  ADDITIONAL_NAMED_INSURED = 'ADDITIONAL_NAMED_INSURED',
  SPECIFIED_BREACH_COUNSEL = 'SPECIFIED_BREACH_COUNSEL',
  SPECIFIED_CYBERSECURITY_FORENSIC_PROVIDER = 'SPECIFIED_CYBERSECURITY_FORENSIC_PROVIDER',
  AMEND_COOPERATION = 'AMEND_COOPERATION',
  AMEND_NOTICE_PROVISION = 'AMEND_NOTICE_PROVISION',
  SPECIFIC_VENDOR_ENDORSEMENT = 'SPECIFIC_VENDOR_ENDORSEMENT',

  SCHEDULED_WAIVER_OF_SUBROGATION = 'SCHEDULED_WAIVER_OF_SUBROGATION',
  SPECIFIC_ENTITY_EXCLUSION = 'SPECIFIC_ENTITY_EXCLUSION',
  SPECIFIED_EVENT_EXCLUSION_ENDORSEMENT = 'SPECIFIED_EVENT_EXCLUSION_ENDORSEMENT',
}

type AmendatoryCoverageSchema = {
  [key in AmendatoryCoverages]: Nullable<ConfigMapping>;
};

type ConfigMapping = {
  Component: (props: AmendatoryLineItemProps) => JSX.Element;
  modal: any;
};

export enum ContextTypes {
  UNDERWRITER = 'Underwriter',
  ENDORSEMENTS = 'Endorsements',
  SIMPLE = 'Simple',
}

export class AmendatoryCoverageManager {
  private productType: PrimeXProductType;

  private context: ContextTypes;

  public amendatoryCoverageSchema: AmendatoryCoverageSchema;

  constructor(
    productType: PrimeXProductType,
    context: ContextTypes = ContextTypes.UNDERWRITER
  ) {
    this.productType = productType;
    this.context = context;

    this.amendatoryCoverageSchema = {
      [AmendatoryCoverages.ADDITIONAL_INSURED]: {
        Component: AdditionalInsureds,
        modal: RaterXAdditionalInsuredsModalConfig,
      },
      [AmendatoryCoverages.AMEND_INSURING_AGREEMENT]: {
        Component: AmendInsuringAgreement,
        modal: RaterXAmendInsuringAgreementModalConfig,
      },
      [AmendatoryCoverages.SPECIFIED_EVENT_EXCLUSION]: {
        Component: SpecifiedEventExclusion,
        modal: RaterXSpecifiedEventExclusionModalConfig,
      },
      [AmendatoryCoverages.SPECIFIED_ENTITY_EXCLUSION]: {
        Component: SpecifiedEntityExclusion,
        modal: RaterXSpecifiedEntityExclusionsModalConfig,
      },
      [AmendatoryCoverages.WAIVER_OF_SUBROGATION]: this.getAmendatoryConfig({
        [ProductTypes.prime_tech]: {
          Component: WaiverOfSubrogation,
          modal: RaterXWaiverOfSubrogationModalConfig,
        },
      }),
      [AmendatoryCoverages.ADDITIONAL_NAMED_INSURED]: {
        Component: AdditionalNamedInsured,
        modal: RaterXAdditionalNamedInsuredModalConfig(this.context),
      },
      [AmendatoryCoverages.DEFENSE_COUNSEL]: {
        Component: DefenseCounsel,
        modal: RaterXDefenseCounselModalModalConfig,
      },
      [AmendatoryCoverages.SPECIFIED_BREACH_COUNSEL]: {
        Component: DefenseCounsel,
        modal: RaterXDefenseCounselModalModalConfig,
      },
      [AmendatoryCoverages.SPECIFIED_CYBERSECURITY_FORENSIC_PROVIDER]: {
        Component: SpecifiedCyberOrForensicProvider,
        modal: RaterXCyberForensicProviderModalConfig,
      },
      [AmendatoryCoverages.AMEND_COOPERATION]: {
        Component: AmendCooperation,
        modal: null,
      },
      [AmendatoryCoverages.AMEND_NOTICE_PROVISION]: {
        Component: AmendNoticeProvision,
        modal: null,
      },
      [AmendatoryCoverages.SPECIFIC_VENDOR_ENDORSEMENT]: {
        Component: SpecificVendorEndorsement,
        modal: RaterXSpecificVendorEndorsementModalConfig,
      },
      [AmendatoryCoverages.SCHEDULED_WAIVER_OF_SUBROGATION]: {
        Component: (props: AmendatoryLineItemProps) => (
          <WaiverOfSubrogation
            {...props}
            hasAdjustment={this.productType !== ProductTypes.prime_one_us}
          />
        ),
        modal: RaterXWaiverOfSubrogationModalConfig,
      },
      [AmendatoryCoverages.SPECIFIC_ENTITY_EXCLUSION]: {
        Component: (props: AmendatoryLineItemProps) => (
          <SpecifiedEntityExclusion
            {...props}
            showDescription={this.productType !== ProductTypes.prime_one_us}
          />
        ),
        modal: RaterXSpecifiedEntityExclusionsModalConfig,
      },
      [AmendatoryCoverages.SPECIFIED_EVENT_EXCLUSION_ENDORSEMENT]: {
        Component: SpecifiedEventExclusionV2,
        modal: RaterXSpecifiedEventExclusionV2ModalConfig,
      },
    };
  }

  private getAmendatoryConfig(map: Record<string, ConfigMapping>) {
    const _config = map[this.productType as keyof typeof map];

    return _config ?? null;
  }
}
